import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Sparkles, Copy, Check, Gift, DollarSign, Users, Link as LinkIcon, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";

/**
 * Referrals tab — Artist Dashboard.
 * Each artist gets a unique referral code. When another creator signs up
 * using it, BOTH parties earn a $500 fee-free sales waiver.
 */
const ReferralsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/referrals/me`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(key);
      toast.success("Copied!");
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const applyReferral = async () => {
    const t = applyCode.trim().toUpperCase();
    if (!t) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/referrals/apply`, { code: t }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Applied — you have $${res.data.waiver_usd.toFixed(2)} in fee-free sales.`);
      setApplyCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply code");
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <div className="card p-12 text-center text-[#8A92A0]">Loading...</div>;
  if (!data) return null;

  const code = data.referral_code || "";
  const remainingUsd = data.referral_waiver_remaining_usd || 0;
  const totalUsd = data.referral_waiver_total_usd || 500;
  const percentRemaining = totalUsd > 0 ? (remainingUsd / totalUsd) * 100 : 0;
  const percentUsed = totalUsd > 0 ? 100 - percentRemaining : 0;
  const consumedUsd = totalUsd - remainingUsd;
  const shareLink = `${window.location.origin}/auth?type=artist&ref=${encodeURIComponent(code)}`;

  return (
    <div className="space-y-8" data-testid="referrals-tab">
      <header className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#EC008C]/15 flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-[#EC008C]" />
        </div>
        <div>
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-1">Referral Rewards</h1>
          <p className="text-[#8A92A0] text-sm max-w-2xl">
            Invite a fellow artist. When they sign up with your code, <span className="text-white font-semibold">both of you keep 100% of your next $500 in gross sales</span> — StardomFans waives the platform fee automatically.
          </p>
        </div>
      </header>

      {/* Progress card */}
      {(data.referral_count > 0 || remainingUsd > 0 || data.referred_by_code) && (
        <div className="card p-6" data-testid="referral-progress-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#8DB87A]" />
              <span className="text-white font-semibold text-lg" data-testid="referral-remaining-usd">${remainingUsd.toFixed(2)} fee-free sales remaining</span>
            </div>
            <span className="text-xs text-[#8A92A0]">Cap: ${totalUsd.toFixed(2)}</span>
          </div>
          <Progress value={percentUsed} className="h-2 bg-[#0A0E18]" />
          <p className="text-xs text-[#8A92A0] mt-3">
            You&apos;ve used <span className="text-white font-semibold">${consumedUsd.toFixed(2)}</span> of your fee-free window.
            <span className="text-[#8DB87A] ml-1">Every sale during this window keeps 100% instead of 90%.</span>
          </p>
        </div>
      )}

      {/* Your referral code */}
      <div className="card p-6" data-testid="referral-code-card">
        <h2 className="text-white font-semibold mb-2">Your Referral Code</h2>
        <p className="text-xs text-[#8A92A0] mb-4">Share this code (or the link below). When a new artist signs up with it, you both unlock $500 of fee-free sales.</p>
        <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-3">
          <div className="flex-1 bg-[#050810] border border-[#1A2030] rounded-xl px-4 py-3 flex items-center gap-3">
            <LinkIcon className="w-4 h-4 text-[#EC008C]" />
            <code className="text-white font-mono text-lg font-bold tracking-wider" data-testid="referral-code">{code}</code>
          </div>
          <Button onClick={() => copy(code, "code")} className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full" data-testid="copy-code-btn">
            {copiedField === "code" ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy Code</>}
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch gap-3">
          <div className="flex-1 bg-[#050810] border border-[#1A2030] rounded-xl px-4 py-3 text-white text-sm break-all">
            {shareLink}
          </div>
          <Button onClick={() => copy(shareLink, "link")} variant="outline" className="border-[#1A2030] text-white hover:border-[#EC008C] rounded-full" data-testid="copy-link-btn">
            {copiedField === "link" ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy Link</>}
          </Button>
        </div>
      </div>

      {/* Referrals list */}
      <div className="card p-6" data-testid="referral-list-card">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-[#EC008C]" />
          <h2 className="text-white font-semibold">Artists You&apos;ve Referred</h2>
          <span className="ml-auto text-xs text-[#8A92A0]" data-testid="referral-count">{data.referral_count || 0} total</span>
        </div>
        {(!data.referrals || data.referrals.length === 0) ? (
          <p className="text-[#8A92A0] text-sm text-center py-6">No referrals yet. Share your code above to get started.</p>
        ) : (
          <div className="space-y-2">
            {data.referrals.map((r) => (
              <div key={r.user_id} className="flex items-center justify-between bg-[#050810] border border-[#1A2030] rounded-xl px-4 py-3">
                <div>
                  <p className="text-white text-sm font-medium">{r.name}</p>
                  <p className="text-xs text-[#8A92A0]">{r.email}</p>
                </div>
                <span className="text-xs text-[#8A92A0]">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply someone else's code */}
      {!data.referred_by_code && (
        <div className="card p-6" data-testid="apply-referral-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-[#EC008C]" />
            <h2 className="text-white font-semibold">Got invited by another artist?</h2>
          </div>
          <p className="text-xs text-[#8A92A0] mb-4">Enter their referral code to activate your $500 fee-free window. One-time only.</p>
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <Input
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="SF..."
              className="bg-[#050810] border-[#1A2030] text-white font-mono uppercase"
              data-testid="apply-code-input"
              maxLength={16}
            />
            <Button onClick={applyReferral} disabled={applying || !applyCode.trim()} className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full" data-testid="apply-code-btn">
              {applying ? "Applying..." : <><Sparkles className="w-4 h-4 mr-1" /> Redeem</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralsTab;
